import API from '../../../api/endpoints.js'
import event from '../../../event.js'
import { createRouteIdHash, transformQueryString, decryptQueryString } from '../../helpers/RouteHash'

export default {
  data () {
    return {
      tableData: [],
      maxValue: 0,
      isFetching: false,
      page: 1,
      orderBy: 'name'
    }
  },
  computed: {
    dataFullyLoaded () {
      return this.tableData.length === this.maxValue
    },
    query () {
      return this.decrypt(this.$route.query)
    },
    reqBody () {
      return !this.isFullSearch ? this.query['quick-search'] : JSON.parse(this.query['search'])
    },
    queryObj () {
      return this.query['quick-search'] ? this.query : JSON.parse(this.query['search'])
    },
    isFullSearch () {
      return !this.query.hasOwnProperty('quick-search')
    },
    /*
    * Some late change requests from client and/or lead
    * means the generic single routine search function
    * needs revising. This allows for overlap of
    * search for some fields where value IN
    * along with items where EQUALS
    */
    dataWithExactMatches () {
      if (!this.hasOwnProperty('exactMatchesRequired') || !this.isFullSearch) {
        return this.tableData
      }
      let data = []
      this.tableData.forEach(obj => {
        let doesMatch = true
        this.exactMatchesRequired.forEach(({ entityProp, relatedPropId }) => {
          if (this.queryObj.hasOwnProperty(entityProp) && this.queryObj[entityProp] !== null && this.queryObj[entityProp] !== '') {
            if (typeof obj[entityProp] === 'object' && obj[entityProp].hasOwnProperty(relatedPropId) && obj[entityProp][relatedPropId] !== this.queryObj[entityProp]) {
              doesMatch = false
            } else if (typeof obj[entityProp] !== 'object' && obj[entityProp] !== this.queryObj[entityProp]) {
              doesMatch = false
            }
          }
        })
        if (doesMatch) {
          data.push(obj)
        }
      })
      return data
    }
  },
  methods: {
    loadMore () {
      if (!this.isFetching && !this.dataFullyLoaded) {
        this.isFetching = true
        this.page++
        const data = !this.isFullSearch ? this.quickSearch({ 'X-Limit': '500', 'X-Page-Number': String(this.page) })
          : this.fullSearch({ 'X-Limit': '500', 'X-Page-Number': String(this.page) })
        data.then(({data}) => {
          this.tableData.push(...data)
        }).catch(err => {
          console.log(err)
        }).then(() => {
          this.isFetching = false
        })
      }
    },
    getResults () {
      if (!this.entityName) {
        window.console.warn(`If you are seeing this message, component '${this.$options.name}' has not overwritten the 'entityName' property`)
      } else {
        this.page = 1
        const params = this.decrypt(this.$route.query)
        this.loading = true
        this.isFetching = true
        const data = !this.isFullSearch ? this.quickSearch({ 'X-Limit': '500', 'X-Page-Number': '1' })
          : this.fullSearch({ 'X-Limit': '500', 'X-Page-Number': '1' })
        data.then(({data}) => {
          this.tableData = data
        }).catch(err => {
          this.notifyResults(false)
          console.log(err)
        }).then(() => {
          this.loading = false
          this.isFetching = false
          this.fetchMaxValue()
        })
      }
    },
    transformFilterRequestData (data) {
      return transformQueryString(data)
    },
    decrypt (params) {
      return decryptQueryString(params)
    },
    quickSearch (opts) {
      return this.$http.get(API.GET_ADMIN_ENTITY_BY_QUICK_SEARCH(this.entityName, this.reqBody || ''), {
        headers: {
          'X-Lazy-Fetch': this.lazy || '1',
          'X-Order-By': this.orderBy,
          ...opts
        }
      })
    },
    fullSearch (opts) {
      let url = ''
      if (this.hasOwnProperty('shouldSearchForExactMatch') && this.shouldSearchForExactMatch) {
        url = API.GET_ENTITIES(this.entityName)
      } else {
        url = API.GET_ADMIN_ENTITY_BY_FULL_SEARCH(this.entityName)
      }
      return this.$http.get(url, {
        params: this.clearEmpties(this.reqBody),
        headers: {
          'X-Lazy-Fetch': this.lazy || '1',
          'X-Order-By': this.orderBy,
          ...opts
        }
      })
    },
    fetchMaxValue () {
      const data = this.isFullSearch ? this.fullSearch({ 'X-Count-Fetch': 'true' })
        : this.quickSearch({ 'X-Count-Fetch': 'true' })
      data.then(({data}) => {
        this.notifyResults(true, data.value)
        this.maxValue = data.value
      })
      .catch(err => console.log(err))
    },
    notifyResults (succeeded, length = 0) {
      if (succeeded) {
        event.$emit('toast', { type: 'success', message: `Your search returned ${length} result(s)` })
      } else {
        event.$emit('toast', { type: 'error', message: 'An error occurred; please try again or contact support' })
      }
    },
    clearEmpties (obj) {
      let o = {}
      for (let prop in obj) {
        if (typeof obj[prop] === 'undefined' || obj[prop] === '' || obj[prop] === null) {
          continue
        }
        o[prop] = obj[prop]
      }
      return o
    },
    removeFromData (eId) {
      const index = this.tableData.findIndex(({id}) => eId === id)
      if (index > -1) {
        this.tableData.splice(index, 1)
      }
    },
    preDeleteRoutine (item) {
      return new Promise((resolve, reject) => resolve())
    },
    postDeleteRoutine (item) {
      return new Promise((resolve, reject) => resolve())
    },
    deleteEntity ({item}) {
      this.preDeleteRoutine(item).then(res => {
        this.$http.delete(API.DELETE_ADMIN_ENTITY(this.entityName, item.id))
        .then(({data}) => {
          event.$emit('toast', { type: 'success', message: 'Entity deleted' })
          this.removeFromData(item.id)
          this.$emit('calibrate-table')
          this.postDeleteRoutine(data)
        }).catch(err => {
          console.log(err)
          event.$emit('toast', { type: 'error', message: 'Entity not deleted' })
        })
      }).catch(err => {
        event.$emit('toast', { type: 'error', message: 'Entity not deleted' })
        console.log(err)
      })
    },
    handleEdit ({item, index}) {
      this.$router.push({ name: this.editEntityScreen, params: {id: createRouteIdHash(item.id)} })
    }
  },
  watch: {
    '$route' (newVal, oldVal) {
      this.getResults()
    }
  },
  mounted () {
    this.getResults()
    event.$on('update-entity-data', this.getResults)
  },
  beforeDestroy () {
    event.$off('update-entity-data', this.getResults)
  }

}



// WEBPACK FOOTER //
// ./src/modules/admin/mixins/AdminSearchResultsMixin.js